<script setup lang="ts">
    import { computed, onMounted, ref, watch, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue'
    import { ProjectAcceptance } from '@common/types'

    import { useApprovalPageQueryV1 } from '@Quest/PortfolioScenarios/queries-vue-composables'

    import
    {
        useApproveScenarioCommandV1,
        useApproveScenarioProjectCommandV1,
        useUpdatePortfolioScenarioCommandV1
    } from '@Quest/PortfolioScenarios/commands-vue-composables'

    import type { ApprovalPageReadModel, ApprovalProjectReadModel } from '@Quest/PortfolioScenarios/readmodels'


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))


    // ======================
    // Query
    // ======================

    const {
        runApprovalPageQueryV1,
        modelApprovalPageQueryV1
    } = useApprovalPageQueryV1(workspaceId, tchProjectId, scenarioId)

    const rm = modelApprovalPageQueryV1 as Ref<ApprovalPageReadModel | null>
    const portfolioId = computed(() => rm.value?.PortfolioId)


    // ======================
    // Commands
    // ======================

    const {
        invokeApproveScenarioCommandV1,
        onSucceedApproveScenarioCommandV1,
    } = useApproveScenarioCommandV1(workspaceId, portfolioId, scenarioId)

    const {
        invokeApproveScenarioProjectCommandV1,
        onSucceedApproveScenarioProjectCommandV1
    } = useApproveScenarioProjectCommandV1(workspaceId, portfolioId, scenarioId)



    // ======================
    // Local State
    // ======================

    const searchPhrase = ref<string | null>(null)
    const filter = ref<string>('All')

    const projectsView = computed<ApprovalProjectReadModel[]>(() =>
    {
        let view = [...(rm.value?.Projects ?? [])]

        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            view = view.filter(a => a.Name.toLowerCase().indexOf(phrase) > -1)
        }

        switch (filter.value)
        {
            case 'All':
                return view

            case 'Accepted':
                return view.filter(a => a.Acceptance === ProjectAcceptance.Accepted)

            case 'Rejected':
                return view.filter(a => a.Acceptance === ProjectAcceptance.Rejected)

            case 'TBD':
                return view.filter(a => a.Acceptance === ProjectAcceptance.TBD)

            default:
                return []
        }
    })


    // ======================
    // Actions
    // ======================

    function handleApproveScenarioCommand()
    {
        if (confirm("Are you sure you want to approve all changes to projects in this scenario?"))
        {
            invokeApproveScenarioCommandV1()
        }
    }

    function handleApproveScenarioProjectCommand(projectId: string)
    {
        invokeApproveScenarioProjectCommandV1({
            ProjectId: projectId
        })
    }

    function onSelected()
    {
        runApprovalPageQueryV1()
    }

    onMounted(() =>
    {
        document.title = "Portfolio Plan - Approval"
        runApprovalPageQueryV1()
    })

    onSucceedApproveScenarioProjectCommandV1(() =>
    {
        runApprovalPageQueryV1()
        alert("Updated")
    })

    onSucceedApproveScenarioCommandV1(() =>
    {
        runApprovalPageQueryV1()
        alert("Updated")
    })
</script>

<template>
    <div>
        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">{{rm.PortfolioName}} / {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Plan: Approval</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <PortfolioScenarioNavigation :portfolioId="rm.PortfolioId" 
                                                 :corporateScenarioUrl="rm.CorporateScenarioUrl"
                                                 @selected="onSelected" />
                </div>
            </div>

            <hr />

            <nav class="navbar navbar-light navbar-expand-md  mt-5" style="background-color: rgba(0, 0, 0, 0.03); border: 1px solid rgba(0, 0, 0, 0.125); border-bottom: 0;" role="navigation">
                <ul class="navbar-nav">

                    <li class="dropdown mr-2">
                        <div class="navbar-form">
                            <button type="button" class="btn btn-outline-success" @click="handleApproveScenarioCommand">
                                <i class="fa-solid fa-pen-nib"></i> Update all
                            </button>
                        </div>
                    </li>

                </ul>

                <ul class="navbar-nav ml-auto mr-2">
                    <li class="dropdown">
                        <div class="navbar-form">
                            <div class="btn-group">
                                <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">View: {{filter}}</button>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="filter = 'All'">All</a>
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Filter by status:</h6>
                                    <a class="dropdown-item" @click="filter = 'TBD'">TBD</a>
                                    <a class="dropdown-item" @click="filter = 'Accepted'">Accepted</a>
                                    <a class="dropdown-item" @click="filter = 'Rejected'">Rejected</a>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
                <form class="form-inline my-2 my-lg-0" role="search">
                    <input class="form-control mr-sm-2" type="search" placeholder="Search" v-model="searchPhrase">
                </form>
            </nav>


            <table class="table table-bordered table-hover">
                <thead>
                    <tr>
                        <th>Project</th>
                        <th>P&L</th>
                        <th>Acceptance</th>
                        <th>Start date</th>
                        <th>Commitment date</th>
                        <th>Status</th>
                        <th style="width: 120px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="a in projectsView" :key="a.Id">
                        <td>
                            <a class="text-primary" v-sanitizedHref="`/#/portfolio/alternative/dashboard/${workspaceId}/${tchProjectId}/${a.Id}`">
                                {{a.Name}}
                            </a>
                        </td>
                        <td>
                            <a v-if="a.PlanId" class="text-primary" v-sanitizedHref="`/#/q/project/pnl/${workspaceId}/${tchProjectId}/${a.Id}/${a.PlanId}`">
                                View
                            </a>
                            <a v-else class="text-danger" v-sanitizedHref="`/#/q/project/summary/${workspaceId}/${tchProjectId}/${a.Id}`">
                                NA
                            </a>
                        </td>
                        <td>{{a.Acceptance}}</td>
                        <td :class="{'table-warning': a.ScenarioStartDateString !== a.PortfolioStartDateString}">
                            {{a.ScenarioStartDateString}}<br />
                            <small class="text-muted" v-if="a.PortfolioStartDateString">
                                ({{a.PortfolioStartDateString}})
                            </small>
                        </td>
                        <td :class="{'table-warning': a.ScenarioCommitmentDateString !== a.PortfolioCommitmentDateString}">
                            {{a.ScenarioCommitmentDateString}}<br />
                            <small class="text-muted" v-if="a.PortfolioCommitmentDateString">
                                ({{a.PortfolioCommitmentDateString}})
                            </small>
                        </td>
                        <td :class="{'table-warning': a.ScenarioKanbanStatus !== a.PortfolioKanbanStatus}">
                            {{a.ScenarioKanbanStatus}}<br />
                            <small class="text-muted" v-if="a.PortfolioKanbanStatus">
                                ({{a.PortfolioKanbanStatus}})
                            </small>
                        </td>

                        <td>
                            <button v-if="a.IsApproved === false" class="btn btn-outline-success" @click="handleApproveScenarioProjectCommand(a.Id)">
                                Update
                            </button>
                            <button v-if="a.IsApproved === true" class="btn btn-success" disabled @click="handleApproveScenarioProjectCommand(a.Id)">
                                Update
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <br />
        <br />
        <br />
        <br />

    </div>

</template>

<style scoped>
    th { 
        text-align: center; 
    }

    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }

</style>
